import { Question, Section, TestCase } from "@src/pages/student/data.type";

export interface QuizRunPayload {
  studentId: string;
  userCode: string;
  questionId: string;
  versionId?: string;
  labels: string[];
  testCase: TestCase[];
  language: string;
  isPracticeQuestion?: boolean;
  questionProgramType?: string;
}

interface AssessmentTestCase {
  input: string;
  expectedOutput: string;
  actualOutput: string;
  result: boolean;
  error: string;
  testCaseType: string;
  testCaseScope: string;
}

export interface UnifiedAssessmentResponse {
  id: string;
  userCode: string;
  answerCode?: string;
  options?: string[];
  correctOptions?: string[];
  marks: number;
  testCaseId: string;
  testCases: AssessmentTestCase[];
  studentId: string;

  // Optional IDs for different assessment types
  assessmentId?: string;
  batchId?: string;
  tournamentId?: string;
  quizId?: string;

  questionId: string;
  versionID: string;
  sectionName?: string;

  syntaxContribution: number;
  namingConventionContribution: number;
  logicContribution: number;
  compilationError: string;

  totalScore: number;
  status: string;
  userOutput: string;
  result: string | boolean;

  // Optional metadata
  createdOn?: string;
  lastUpdateOn?: string;
  createdByName?: string;
  createdByEmail?: string;
  modifiedByName?: string;
  modifiedByEmail?: string;

  questionSubmitDuration?: number;
  failedAttempts?: number;
  totalAttempts?: number;
  totalTestCase?: number;
  passedTestCase?: number;
  questionNo?: number;
  runtimeError?: string;
  penalty?: string | number;
  questionProgramType?: string;
}

export interface AdditionalDetailsPayload {
  [key: string]:
    | string
    | string[]
    | boolean
    | { label: string; value: string }
    | Date;
}

export const resolveFirstUnanswered = (
  sections: Section[],
  startIndex = 0,
): { sectionIndex: number; question: Question } | null => {
  for (let i = startIndex; i < sections.length; i++) {
    const section = sections[i];
    if (!section?.questionResponseList?.length) continue;

    const unanswered = section.questionResponseList.find(
      (q) => q.answered !== true,
    );

    if (unanswered) {
      return {
        sectionIndex: i,
        question: { ...unanswered, notAnswered: true, notVisited: false },
      };
    }
  }

  const lastSection = sections[sections.length - 1];
  if (!lastSection?.questionResponseList?.length) return null;

  return {
    sectionIndex: sections.length - 1,
    question:
      lastSection.questionResponseList[
        lastSection.questionResponseList.length - 1
      ],
  };
};

export const getPsychometricOrderedQuestions = (
  questions: Question[],
  quizType?: string,
): Question[] => {
  if (quizType !== "PSYCHOMETRIC") {
    return questions;
  }

  const answered: Question[] = [];
  const unanswered: Question[] = [];

  for (const q of questions) {
    if (q.answered === true) {
      answered.push(q);
    } else {
      unanswered.push(q);
    }
  }

  return answered.concat(unanswered);
};

export const getSortedQuestion = (
  questions: Question[],
  quizType?: string,
): Question[] => {
  if (quizType !== "PSYCHOMETRIC") {
    return questions;
  }

  const answered: Question[] = [];
  const unanswered: Question[] = [];

  for (const q of questions) {
    if (q.answered === true) {
      answered.push(q);
    } else {
      unanswered.push(q);
    }
  }

  return answered.concat(unanswered);
};
